import { HttpService } from 'http/httpService'
import { addDaysIso, daysBetween } from './dates'

export type ForecastTarget = 'revenue' | 'visitors'
export type ForecastCadence = 'monthly' | 'weekly' | 'daily'

export interface WeatherRow {
    series_id: string
    ds: string
    condition?: string | null
    icon?: string | null
    temp_max: number | null
    temp_min: number | null
    precip_mm: number | null
    precip_chance: number | null
    humidity: number | null
    wind_speed: number | null
}

export interface ForecastRow {
    series_id: string
    ds: string
    ds_end: string
    yhat: number
    yhat_p10: number
    yhat_p90: number
    method: string | null
    venue_closed?: boolean | null
}

/** the venue's own open/closed schedule for one day — what the checkout
 *  widget applies, not a Pythia prediction */
export interface VenueCalendarRow {
    series_id: string
    ds: string
    is_open: boolean
    closure_reason: string | null
    timezone: string | null
    opening_time_local: string | null
    closing_time_local: string | null
    sell_until_time_local: string | null
}

interface GetForecastsParams {
    target: ForecastTarget
    cadence: ForecastCadence
    account: string
    limit?: number
}

export interface HolidayRow {
    series_id: string
    ds: string
    school_holiday_share: number
    public_holiday_share: number
    event_days: number
}

export interface ActualRow {
    series_id: string
    ds: string
    value: number | null
}

export interface HindcastRow {
    series_id: string
    cutoff_ds: string
    ds: string
    yhat: number | null
    yhat_p10: number | null
    yhat_p90: number | null
    venue_closed?: boolean | null
}

export interface AsServedRow {
    series_id: string
    ds: string
    ds_end: string | null
    yhat: number | null
    yhat_p10: number | null
    yhat_p90: number | null
    method: string | null
    made_at: string | null
    lead_days: number | null
    weather_aware: boolean | null
    y_actual: number | null
}

interface GetActualsParams {
    target: ForecastTarget
    cadence: ForecastCadence
    account: string
    dateFrom: string
    dateTo: string
    includeIncomplete?: boolean
}

export interface OnBooksRow {
    series_id: string
    ds: string
    on_books_qty: number
}

export interface ProductActualRow {
    series_id: string
    ds: string
    value: number | null
    product_id: string | null
    product_name: string | null
    product_type: string | null
}

export class PythiaService {
    constructor(private httpService: HttpService, private baseUrl: string) {}

    private async get<T>(path: string, params: URLSearchParams, label: string): Promise<T[]> {
        const res = await this.httpService.fetch(`${this.baseUrl}${path}?${params}`, {
            headers: { accept: 'application/json' },
        })
        if (!res.ok) {
            throw new Error(`Pythia ${label} failed: ${res.status}`)
        }
        return res.json()
    }

    private async getActualsPage<T>(
        params: URLSearchParams,
        label: string,
    ): Promise<{ rows: T[]; truncated: boolean }> {
        const res = await this.httpService.fetch(`${this.baseUrl}/actuals?${params}`, {
            headers: { accept: 'application/json' },
        })
        if (!res.ok) {
            throw new Error(`Pythia ${label} failed: ${res.status}`)
        }
        const rows = (await res.json()) as T[]
        const header = res.headers.get('X-Truncated')
        const truncated = header === 'true' || (header == null && rows.length >= 5000)
        return { rows, truncated }
    }

    async getForecasts({ target, cadence, account, limit = 100 }: GetForecastsParams): Promise<ForecastRow[]> {
        const params = new URLSearchParams({
            target,
            cadence,
            accounts: account,
            limit: String(limit),
        })
        const rows = await this.get<ForecastRow>('/forecasts', params, `${target}/${cadence}`)
        return rows.filter((r) => r.series_id === account).sort((a, b) => a.ds.localeCompare(b.ds))
    }

    async getWeather({
        account,
        days = 10,
        pastDays = 0,
    }: {
        account: string
        days?: number
        pastDays?: number
    }): Promise<WeatherRow[]> {
        const params = new URLSearchParams({
            accounts: account,
            days: String(days),
            past_days: String(pastDays),
        })
        const rows = await this.get<WeatherRow>('/weather', params, 'weather')
        return rows.filter((r) => r.series_id === account).sort((a, b) => a.ds.localeCompare(b.ds))
    }

    async getVenueCalendar({
        account,
        dateFrom,
        dateTo,
    }: {
        account: string
        dateFrom: string
        dateTo: string
    }): Promise<VenueCalendarRow[]> {
        const params = new URLSearchParams({ accounts: account, date_from: dateFrom, date_to: dateTo })
        const rows = await this.get<VenueCalendarRow>('/calendar', params, 'venue calendar')
        return rows.filter((r) => r.series_id === account).sort((a, b) => a.ds.localeCompare(b.ds))
    }

    async getHolidays({ account, days = 90 }: { account: string; days?: number }): Promise<HolidayRow[]> {
        const params = new URLSearchParams({ accounts: account, days: String(days) })
        const rows = await this.get<HolidayRow>('/holidays', params, 'holidays')
        return rows.filter((r) => r.series_id === account).sort((a, b) => a.ds.localeCompare(b.ds))
    }

    async getHindcasts({
        target,
        cadence,
        account,
        dateFrom,
        dateTo,
        limit = 5000,
    }: {
        target: ForecastTarget
        cadence: ForecastCadence
        account: string
        dateFrom: string
        dateTo: string
        limit?: number
    }): Promise<HindcastRow[]> {
        const params = new URLSearchParams({
            target,
            cadence,
            accounts: account,
            date_from: dateFrom,
            date_to: dateTo,
            policy: 'served',
            limit: String(limit),
        })
        const rows = await this.get<HindcastRow>('/hindcasts', params, `hindcasts ${target}/${cadence}`)
        return rows
            .filter((r) => r.series_id === account || r.series_id.startsWith(`${account}::`))
            .sort((a, b) => a.ds.localeCompare(b.ds))
    }

    async getAsServed({
        target,
        cadence,
        account,
        dateFrom,
        dateTo,
        limit = 5000,
    }: {
        target: ForecastTarget
        cadence: ForecastCadence
        account: string
        dateFrom: string
        dateTo: string
        limit?: number
    }): Promise<AsServedRow[]> {
        const params = new URLSearchParams({
            target,
            cadence,
            accounts: account,
            date_from: dateFrom,
            date_to: dateTo,
            limit: String(limit),
        })
        const rows = await this.get<AsServedRow>('/forecasts/as-served', params, `as-served ${target}/${cadence}`)
        return rows.filter((r) => r.series_id === account).sort((a, b) => a.ds.localeCompare(b.ds))
    }

    async getActuals({
        target,
        cadence,
        account,
        dateFrom,
        dateTo,
        includeIncomplete = false,
    }: GetActualsParams): Promise<ActualRow[]> {
        const rows = await this.actualsRange({ target, cadence, account, dateFrom, dateTo, includeIncomplete }, 3)
        return rows.filter((r) => r.series_id === account).sort((a, b) => a.ds.localeCompare(b.ds))
    }

    private async actualsRange(
        { target, cadence, account, dateFrom, dateTo, includeIncomplete }: GetActualsParams,
        splitsLeft: number,
    ): Promise<ActualRow[]> {
        const params = new URLSearchParams({
            target,
            cadence,
            accounts: account,
            date_from: dateFrom,
            date_to: dateTo,
            rollup: 'partner',
        })
        if (includeIncomplete && target === 'revenue') {
            params.set('include_incomplete', 'true')
        }
        const { rows, truncated } = await this.getActualsPage<ActualRow>(params, `actuals ${target}/${cadence}`)
        if (!truncated) {
            return rows
        }
        if (splitsLeft > 0 && daysBetween(dateFrom, dateTo) >= 1) {
            const mid = addDaysIso(dateFrom, Math.floor(daysBetween(dateFrom, dateTo) / 2))
            const [head, tail] = await Promise.all([
                this.actualsRange({ target, cadence, account, dateFrom, dateTo: mid, includeIncomplete }, splitsLeft - 1),
                this.actualsRange(
                    { target, cadence, account, dateFrom: addDaysIso(mid, 1), dateTo, includeIncomplete },
                    splitsLeft - 1,
                ),
            ])
            return [...head, ...tail]
        }
        throw new Error(`Pythia actuals ${target} truncated for ${account} ${dateFrom}..${dateTo}`)
    }

    async getVisitorsByProduct({
        account,
        date,
    }: {
        account: string
        date: string
    }): Promise<{ rows: ProductActualRow[]; truncated: boolean }> {
        const params = new URLSearchParams({
            target: 'visitors',
            cadence: 'daily',
            accounts: account,
            date_from: date,
            date_to: date,
            rollup: 'product',
            include_future: 'true',
        })
        const { rows, truncated } = await this.getActualsPage<ProductActualRow>(params, 'actuals visitors/product')
        return {
            rows: rows.filter((r) => r.series_id === account || r.series_id.startsWith(`${account}::`)),
            truncated,
        }
    }

    async getOnBooks({ account, days = 28 }: { account: string; days?: number }): Promise<OnBooksRow[]> {
        const params = new URLSearchParams({ accounts: account, days: String(days) })
        const rows = await this.get<OnBooksRow>('/forecasts/on-books', params, 'on-books')
        return rows.filter((r) => r.series_id === account).sort((a, b) => a.ds.localeCompare(b.ds))
    }
}
